import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Dumbbell, Plus, Trash2, Flame, ChevronDown, ChevronUp, Zap, Activity, History, Link2, Unlink
} from 'lucide-react';
import { toast } from 'sonner';
import ExerciseSearchSelect from '../components/ExerciseSearchSelect';
import CustomExerciseModal from '../components/CustomExerciseModal';
import WodSection from '../components/WodSection';

const CHART_TICK = { fill: '#71717a', fontSize: 10 };
const CHART_AXIS = { stroke: '#27272a' };
const CHART_TOOLTIP = { backgroundColor: '#111118', border: '1px solid #27272a', borderRadius: '2px', color: '#fff' };

const CATEGORY_COLORS = {
  machine: '#3b82f6', poids_libres: '#f59e0b', poids_du_corps: '#10b981',
  cardio: '#ef4444', fonctionnel: '#8b5cf6', halterophilie: '#ec4899',
  abdos_core: '#06b6d4', stretching: '#84cc16', sport: '#f97316',
};

export default function ExercisesPage({ embedded = false }) {
  const [catalog, setCatalog] = useState(null);
  const [todayExercises, setTodayExercises] = useState({ exercises: [], totals: { effort_score: 0, exercise_count: 0, total_sets: 0, total_reps: 0 } });
  const [effortData, setEffortData] = useState({ history: [], stats: { avg_daily_effort: 0, active_days: 0 } });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showWods, setShowWods] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customInitialName, setCustomInitialName] = useState('');

  // Form state
  const [selectedCategory, setSelectedCategory] = useState('machine');
  const [selectedExercise, setSelectedExercise] = useState('');
  const [formSets, setFormSets] = useState('');
  const [formReps, setFormReps] = useState('');
  const [formWeight, setFormWeight] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Superset state
  const [supersetGroupId, setSupersetGroupId] = useState(null);
  const [supersetPosition, setSupersetPosition] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [catalogRes, todayRes, effortRes] = await Promise.all([
        apiClient.get('/exercises/catalog'),
        apiClient.get('/exercises/today'),
        apiClient.get('/effort/daily?days=30')
      ]);
      setCatalog(catalogRes.data);
      setTodayExercises(todayRes.data);
      setEffortData(effortRes.data);
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Set default exercise when category changes
  useEffect(() => {
    if (catalog && catalog[selectedCategory]?.exercises?.length > 0 && !selectedExercise) {
      setSelectedExercise(catalog[selectedCategory].exercises[0].id);
    }
  }, [selectedCategory, catalog, selectedExercise]);

  const currentExInfo = catalog ? (() => {
    for (const catData of Object.values(catalog)) {
      const found = catData.exercises?.find(e => e.id === selectedExercise);
      if (found) return found;
    }
    return null;
  })() : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedExercise || !formReps) {
      toast.error('Exercice et valeur requis');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        exercise_type: selectedExercise,
        reps: parseInt(formReps),
        sets: parseInt(formSets) || 1,
        weight: formWeight ? parseFloat(formWeight) : null,
        notes: formNotes || null,
        superset_group_id: supersetGroupId,
        superset_position: supersetPosition || null,
      };
      await apiClient.post('/exercises', payload);
      toast.success('Exercice enregistre !');
      // If in superset mode, increment position for next exercise
      if (supersetGroupId) {
        setSupersetPosition(prev => prev + 1);
      }
      setFormReps('');
      setFormSets('');
      setFormWeight('');
      setFormNotes('');
      fetchData();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteExercise = async (id) => {
    try {
      await apiClient.delete(`/exercises/${id}`);
      toast.success('Exercice supprime');
      fetchData();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const startSuperset = () => {
    const id = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2);
    setSupersetGroupId(id);
    setSupersetPosition(1);
    toast.success('Mode superset active — enregistre tes exercices');
  };

  const endSuperset = () => {
    setSupersetGroupId(null);
    setSupersetPosition(0);
    toast.info('Superset termine');
  };

  const handleCustomExerciseCreated = () => {
    fetchData(); // Reload catalog
  };

  const handleOpenCustom = (prefillName) => {
    setCustomInitialName(prefillName || '');
    setShowCustomModal(true);
  };

  const handleUseWod = async (wodData) => {
    // The WOD exercises are pre-filled — user can log them one by one
    const exs = wodData.exercises || [];
    if (exs.length === 0) return;
    // Log all exercises from WOD
    let logged = 0;
    for (const ex of exs) {
      try {
        await apiClient.post('/exercises', {
          exercise_type: ex.exercise_id,
          reps: parseInt(ex.reps) || 10,
          sets: ex.sets || 3,
          weight: null,
          notes: `WOD: ${wodData.wod_name}`,
        });
        logged++;
      } catch { /* continue */ }
    }
    if (logged > 0) {
      toast.success(`${logged} exercices enregistres depuis le WOD`);
      fetchData();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const t = todayExercises.totals;
  const s = effortData.stats;

  // Group today's exercises by superset
  const groupedExercises = (() => {
    const exercises = todayExercises.exercises || [];
    const groups = [];
    const supersetMap = {};
    for (const ex of exercises) {
      if (ex.superset_group_id) {
        if (!supersetMap[ex.superset_group_id]) {
          supersetMap[ex.superset_group_id] = { type: 'superset', exercises: [] };
          groups.push(supersetMap[ex.superset_group_id]);
        }
        supersetMap[ex.superset_group_id].exercises.push(ex);
      } else {
        groups.push({ type: 'single', exercises: [ex] });
      }
    }
    return groups;
  })();

  return (
    <div className="min-h-screen bg-[#0F0F0F] pb-20">
      <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <p className="text-xs text-[#FF6B35] uppercase tracking-widest">Entrainement</p>
            <h1 className="text-xl font-bold text-white">Mes exercices</h1>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowForm(!showForm)}
              data-testid="add-exercise-button"
              className="bg-[#FF6B35] text-black hover:bg-[#E85A25]"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter
            </Button>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4 animate-fade-in">
        {/* Daily Effort Summary */}
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] border-t-2 border-t-[#FF6B35] rounded-sm p-4" data-testid="daily-effort-summary">
          <h3 className="text-sm font-medium text-zinc-400 mb-3">Effort du jour</h3>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <Flame className="w-3.5 h-3.5 text-[#FF6B35]" />
              </div>
              <p className="font-mono text-lg font-bold text-white">{t.effort_score.toLocaleString()}</p>
              <p className="text-xs text-zinc-400">effort</p>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <Dumbbell className="w-3.5 h-3.5 text-blue-400" />
              </div>
              <p className="font-mono text-lg font-bold text-white">{t.exercise_count}</p>
              <p className="text-xs text-zinc-400">exercices</p>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <Zap className="w-3.5 h-3.5 text-yellow-400" />
              </div>
              <p className="font-mono text-lg font-bold text-white">{t.total_sets}</p>
              <p className="text-xs text-zinc-400">series</p>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1 mb-1">
                <Activity className="w-3.5 h-3.5 text-green-400" />
              </div>
              <p className="font-mono text-lg font-bold text-white">{t.total_reps}</p>
              <p className="text-xs text-zinc-400">reps</p>
            </div>
          </div>
          {s.avg_daily_effort > 0 && (
            <div className="mt-3 pt-3 border-t border-[#2A2A2E]">
              <div className="flex justify-between text-xs text-zinc-500 mb-1">
                <span>Moyenne journaliere</span>
                <span className="font-mono text-white">{s.avg_daily_effort.toLocaleString()} pts</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-[#FF6B35] rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (t.effort_score / Math.max(s.avg_daily_effort, 1)) * 100)}%` }} />
              </div>
              <div className="flex justify-between text-xs text-zinc-400 mt-1">
                <span>{s.active_days} jours actifs / 30j</span>
                <span>{s.attendance_days || 0} presences</span>
              </div>
            </div>
          )}
        </div>

        {/* Add Exercise Form */}
        {showForm && catalog && (
          <form onSubmit={handleSubmit} className="bg-[#1B1B1B] border border-[#FF6B35]/30 rounded-sm p-4 space-y-4" data-testid="exercise-form">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-[#FF6B35]">Nouvel exercice</h3>
              {/* Superset toggle */}
              {supersetGroupId ? (
                <button type="button" onClick={endSuperset} className="flex items-center gap-1 text-xs text-[#FF6B35] bg-[#FF6B35]/10 px-2.5 py-1 rounded-full" data-testid="end-superset">
                  <Unlink className="w-3 h-3" /> Terminer superset ({supersetPosition - 1} exo{supersetPosition > 2 ? 's' : ''})
                </button>
              ) : (
                <button type="button" onClick={startSuperset} className="flex items-center gap-1 text-xs text-zinc-400 hover:text-[#FF6B35] bg-[#0F0F0F] px-2.5 py-1 rounded-full border border-[#2A2A2E] hover:border-[#FF6B35]/50 transition-colors" data-testid="start-superset">
                  <Link2 className="w-3 h-3" /> Superset
                </button>
              )}
            </div>

            {supersetGroupId && (
              <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-sm px-3 py-2">
                <p className="text-xs text-[#FF6B35]">
                  Mode superset actif — {supersetPosition > 1 ? `${supersetPosition - 1} exercice${supersetPosition > 2 ? 's' : ''} enregistre${supersetPosition > 2 ? 's' : ''}. ` : ''}Enregistre chaque exercice du superset puis clique &laquo;Terminer&raquo;.
                </p>
              </div>
            )}

            {/* Searchable exercise selector */}
            <ExerciseSearchSelect
              catalog={catalog}
              selectedCategory={selectedCategory}
              selectedExercise={selectedExercise}
              onCategoryChange={setSelectedCategory}
              onExerciseChange={setSelectedExercise}
              onCreateCustom={handleOpenCustom}
            />

            {/* Dynamic unit label */}
            {currentExInfo && (
              <p className="text-xs text-zinc-400">
                Muscle : {currentExInfo.muscle} | Unite : {currentExInfo.unit}
              </p>
            )}

            {/* Sets / Reps / Weight */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-500 text-[10px]">Series</Label>
                <Input type="number" value={formSets} onChange={(e) => setFormSets(e.target.value)} placeholder="4" className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" data-testid="sets-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-500 text-[10px]">
                  {currentExInfo?.unit === 'km' ? 'Distance (km)' :
                   currentExInfo?.unit === 'm' ? 'Distance (m)' :
                   currentExInfo?.unit === 'min' ? 'Duree (min)' :
                   currentExInfo?.unit === 'sec' ? 'Duree (sec)' :
                   currentExInfo?.unit === 'tours' ? 'Tours' :
                   currentExInfo?.unit === 'etages' ? 'Etages' :
                   currentExInfo?.unit === 'cal' ? 'Calories' :
                   'Reps *'}
                </Label>
                <Input type="number" value={formReps} onChange={(e) => setFormReps(e.target.value)} placeholder={currentExInfo?.unit === 'kg' ? '12' : '10'} required className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" data-testid="reps-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-500 text-[10px]">Charge (kg)</Label>
                <Input type="number" step="0.5" value={formWeight} onChange={(e) => setFormWeight(e.target.value)} placeholder="0" className="bg-[#0F0F0F] border-[#2A2A2E] text-white font-mono text-sm" data-testid="weight-input" />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-zinc-500 text-[10px]">Notes (optionnel)</Label>
              <Input value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="Sensation, tempo..." className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-sm" data-testid="notes-input" />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1 border-zinc-700 text-zinc-400 hover:bg-zinc-800">
                Annuler
              </Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="save-exercise-button">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        )}

        {/* Today's exercises (grouped by superset) */}
        {todayExercises.exercises.length > 0 && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm" data-testid="today-exercises-list">
            <div className="p-4 border-b border-[#2A2A2E] flex items-center gap-2">
              <Dumbbell className="w-4 h-4 text-[#FF6B35]" />
              <h3 className="text-sm font-medium text-zinc-400">Exercices du jour</h3>
              <span className="ml-auto text-xs text-zinc-500">{todayExercises.exercises.length} exercices</span>
            </div>
            <div className="divide-y divide-zinc-800/50">
              {groupedExercises.map((group, gi) => (
                <div key={gi}>
                  {group.type === 'superset' && (
                    <div className="flex items-center gap-2 px-4 pt-2 pb-1">
                      <Link2 className="w-3 h-3 text-[#FF6B35]" />
                      <span className="text-[10px] text-[#FF6B35] font-semibold uppercase tracking-wider">Superset ({group.exercises.length} exos)</span>
                    </div>
                  )}
                  {group.exercises.map((ex) => {
                    const catColor = CATEGORY_COLORS[ex.category] || '#666';
                    return (
                      <div key={ex.id} className={`p-3 px-4 hover:bg-[#1a1a24] transition-colors group ${group.type === 'superset' ? 'border-l-2 ml-3' : ''}`} style={group.type === 'superset' ? { borderLeftColor: '#FF6B35' } : {}}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: catColor }} />
                              <p className="text-sm text-white font-medium truncate">{ex.exercise_name || ex.exercise_type}</p>
                              {ex.superset_position && (
                                <span className="text-[9px] bg-[#FF6B35]/20 text-[#FF6B35] px-1.5 py-0.5 rounded-full">#{ex.superset_position}</span>
                              )}
                            </div>
                            <div className="flex gap-3 text-xs text-zinc-500">
                              {ex.sets > 1 && <span>{ex.sets} series</span>}
                              <span>{ex.reps} {ex.category === 'cardio' ? 'unites' : 'reps'}</span>
                              {ex.weight && <span>{ex.weight}kg</span>}
                              {ex.one_rm && <span className="text-zinc-400">1RM: {ex.one_rm}kg</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs text-[#FF6B35]">{ex.effort_score?.toLocaleString()}</span>
                            <button
                              onClick={() => deleteExercise(ex.id)}
                              className="opacity-0 group-hover:opacity-100 opacity-60 md:opacity-0 md:group-hover:opacity-100 p-1.5 rounded-sm hover:bg-red-500/20 text-zinc-500 hover:text-red-400 transition-all"
                              data-testid={`delete-exercise-${ex.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        {ex.notes && <p className="text-xs text-zinc-400 mt-1 ml-4">{ex.notes}</p>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {todayExercises.exercises.length === 0 && !showForm && (
          <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-8 text-center" data-testid="no-exercises">
            <Dumbbell className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">Aucun exercice aujourd&#39;hui</p>
            <p className="text-xs text-zinc-500">Clique sur &laquo;Ajouter&raquo; pour logger ta seance</p>
          </div>
        )}

        {/* WOD Toggle */}
        <button
          onClick={() => setShowWods(!showWods)}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm text-sm text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
          data-testid="toggle-wod-section"
        >
          <Flame className="w-4 h-4" />
          WODs & Sets partages
          {showWods ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showWods && <WodSection catalog={catalog} onUseWod={handleUseWod} />}

        {/* Effort History Toggle */}
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm text-sm text-zinc-400 hover:text-white hover:border-zinc-700 transition-colors"
          data-testid="toggle-effort-history"
        >
          <History className="w-4 h-4" />
          Historique d&#39;effort
          {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showHistory && (
          <div className="space-y-4">
            {effortData.history.length > 1 && (
              <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="effort-chart">
                <h4 className="text-xs text-zinc-500 mb-3">Effort par jour (30 derniers jours)</h4>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[...effortData.history].reverse().slice(-14)}>
                      <XAxis dataKey="date" tick={CHART_TICK} axisLine={CHART_AXIS} tickLine={false} tickFormatter={(v) => { const d = new Date(v + 'T12:00:00'); return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }); }} />
                      <YAxis tick={CHART_TICK} axisLine={CHART_AXIS} tickLine={false} width={45} />
                      <Tooltip contentStyle={CHART_TOOLTIP} labelFormatter={(v) => { const d = new Date(v + 'T12:00:00'); return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }); }} />
                      <Bar dataKey="effort_score" fill="#FF6B35" radius={[2, 2, 0, 0]} name="Effort" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm" data-testid="effort-history-list">
              <div className="p-4 border-b border-[#2A2A2E]">
                <h3 className="text-sm font-medium text-zinc-400">Historique detaille</h3>
              </div>
              {effortData.history.length === 0 ? (
                <p className="p-4 text-center text-xs text-zinc-500">Aucun historique</p>
              ) : (
                <div className="divide-y divide-zinc-800/50 max-h-[400px] overflow-y-auto">
                  {effortData.history.map((day) => {
                    const isToday = day.date === new Date(Date.now() - 10 * 3600000).toISOString().slice(0, 10);
                    return (
                      <div key={day.date} className={`p-3 px-4 ${isToday ? 'bg-[#FF6B35]/5' : ''}`}>
                        <div className="flex justify-between items-center mb-1">
                          <p className="text-sm text-white font-medium">
                            {isToday ? "Aujourd'hui" : formatDate(day.date)}
                          </p>
                          <p className="font-mono text-sm font-bold text-[#FF6B35]">{day.effort_score.toLocaleString()} pts</p>
                        </div>
                        <div className="flex gap-3 text-xs text-zinc-500">
                          <span>{day.exercise_count} exercice{day.exercise_count > 1 ? 's' : ''}</span>
                          <span>{day.sets} series</span>
                          <span>{day.reps} reps</span>
                        </div>
                        {day.exercise_names?.length > 0 && (
                          <p className="text-xs text-zinc-400 mt-1 truncate">{day.exercise_names.join(', ')}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Custom Exercise Modal */}
      <CustomExerciseModal
        isOpen={showCustomModal}
        onClose={() => setShowCustomModal(false)}
        onCreated={handleCustomExerciseCreated}
        initialName={customInitialName}
      />

      {!embedded && <BottomNav />}
    </div>
  );
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}
